/**
 * Auto-link country/region names inside blog post HTML to their `/destinations/{id}` page.
 *
 * SEO purpose: creates internal contextual anchor links (Google's strongest
 * on-site ranking signal) between long-tail blog content and commercial
 * destination pages. Each blog mention of "Japan" becomes an internal link
 * to `/destinations/JP`, boosting the destination page's authority for the
 * anchor text.
 *
 * Rules:
 *  • Only the FIRST occurrence of each country per post is linked (avoids
 *    over-optimisation / keyword stuffing penalties).
 *  • Headings (h1–h4) are never modified — keeps them clean and preserves
 *    the reader's semantic flow.
 *  • Text inside existing <a> tags is never touched (no nested links).
 *  • Matches are whole-word, case-insensitive.
 *  • Runs client-side via DOMParser — safe for arbitrary opinly.ai HTML.
 *
 * Returns the modified HTML string.
 */
export const linkifyBlogBody = (html, countries) => {
  if (!html || !Array.isArray(countries) || countries.length === 0) return html || '';

  // Build a lookup keyed by lowercased name.
  // Longer names first so "United Kingdom" wins over "Kingdom" and
  // "United States" beats "States". This prevents partial-name mismatches.
  const sorted = [...countries]
    .filter((c) => c?.name && c?.id)
    .sort((a, b) => b.name.length - a.name.length);

  const remaining = new Map(); // key: lowercased name, value: { id, name, regexBuf }
  for (const c of sorted) {
    const key = c.name.toLowerCase();
    if (!remaining.has(key)) {
      remaining.set(key, { id: c.id, name: c.name });
    }
  }
  if (remaining.size === 0) return html;

  // Parse safely — wrap in a container so we can extract innerHTML back cleanly.
  let doc;
  try {
    doc = new DOMParser().parseFromString(`<div id="__bloglinker">${html}</div>`, 'text/html');
  } catch (e) {
    return html;
  }
  const root = doc.getElementById('__bloglinker');
  if (!root) return html;

  const SKIP_TAG = /^(A|H1|H2|H3|H4|CODE|PRE|SCRIPT|STYLE)$/;

  const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
  const textNodes = [];
  let n;
  while ((n = walker.nextNode())) {
    // Skip any text node whose ancestors include an unwanted element.
    let anc = n.parentNode;
    let skip = false;
    while (anc && anc !== root) {
      if (anc.nodeType === 1 && SKIP_TAG.test(anc.tagName)) {
        skip = true;
        break;
      }
      anc = anc.parentNode;
    }
    if (!skip) textNodes.push(n);
  }

  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  for (const node of textNodes) {
    if (remaining.size === 0) break;
    let text = node.nodeValue;
    if (!text || !text.trim()) continue;

    // Find the earliest match across still-unlinked countries.
    let bestIdx = -1;
    let bestKey = null;
    let bestMatch = null;
    for (const [key, entry] of remaining) {
      const re = new RegExp(`\\b${escape(entry.name)}\\b`, 'i');
      const m = re.exec(text);
      if (m && (bestIdx === -1 || m.index < bestIdx)) {
        bestIdx = m.index;
        bestKey = key;
        bestMatch = m;
      }
    }
    if (bestIdx === -1 || !bestKey) continue;

    const entry = remaining.get(bestKey);
    const before = text.slice(0, bestIdx);
    const matched = bestMatch[0];
    const after = text.slice(bestIdx + matched.length);

    const parent = node.parentNode;
    if (before) parent.insertBefore(doc.createTextNode(before), node);

    const a = doc.createElement('a');
    a.setAttribute('href', `/destinations/${entry.id}`);
    a.setAttribute('data-blog-autolink', entry.id);
    a.textContent = matched;
    parent.insertBefore(a, node);

    if (after) parent.insertBefore(doc.createTextNode(after), node);
    parent.removeChild(node);

    remaining.delete(bestKey);
  }

  return root.innerHTML;
};
